/**
 * End runtime glue (one instance in main.ts): thrown eyes of ender — sprite,
 * flight toward the nearest stronghold (see endPortal.ts EnderEye) and the
 * drop / shatter at the end of it.
 */

import * as THREE from 'three';
import type { EnvUniforms } from '../render/env';
import { itemSpriteMesh } from '../render/entities/extras';
import { nearestStronghold } from '../worldgen/structures/stronghold';
import { EnderEye } from './endPortal';

export interface EndRuntimeHost {
  env: EnvUniforms;
  scene: THREE.Object3D;
  camera: THREE.Camera;
  seed: number;
  dimension(): string;
  /** The eye came down intact: drop an eye of ender item here. */
  dropEye(x: number, y: number, z: number): void;
  /** The eye shattered (or landed): a little burst. */
  burst(x: number, y: number, z: number): void;
}

interface Flying { eye: EnderEye; mesh: THREE.Mesh }

export class EndRuntime {
  private flying: Flying[] = [];

  constructor(private readonly h: EndRuntimeHost) {}

  /** Throw an eye from `from` (the player's eye). False outside the overworld (nothing to point at). */
  throwEye(from: THREE.Vector3): boolean {
    if (this.h.dimension() !== 'overworld') return false;
    const target = nearestStronghold(this.h.seed, from.x, from.z);
    const { mesh } = itemSpriteMesh(this.h.env, 'ender_eye', true);
    mesh.scale.setScalar(0.5);
    this.h.scene.add(mesh);
    this.flying.push({ eye: new EnderEye(from.x, from.y - 0.1, from.z, target, Math.random), mesh });
    return true;
  }

  /** Dimension switch: eyes in flight are gone. */
  clear(): void {
    for (const f of this.flying) f.mesh.removeFromParent();
    this.flying.length = 0;
  }

  tick(): void {
    for (const f of this.flying) {
      f.eye.tick();
      if (!f.eye.done) continue;
      f.mesh.removeFromParent();
      if (f.eye.drops) this.h.dropEye(f.eye.x, f.eye.y, f.eye.z);
      else this.h.burst(f.eye.x, f.eye.y, f.eye.z);
    }
    this.flying = this.flying.filter((f) => !f.eye.done);
  }

  frame(alpha: number): void {
    for (const { eye, mesh } of this.flying) {
      mesh.position.set(eye.px + (eye.x - eye.px) * alpha, eye.py + (eye.y - eye.py) * alpha, eye.pz + (eye.z - eye.pz) * alpha);
      mesh.quaternion.copy(this.h.camera.quaternion);
    }
  }
}
